import { useLocalStorageState } from 'ahooks';
import { useCallback } from 'react';
import { ONE_MONTH } from '@/app/constants';
import { useStore, useStoreApi } from '@/app/providers/store';

interface BonusCodeBound {
  code: string;
  boundAt: number;
}

export const BONUS_CODE_BOUND_KEY = 'BONUS_CODE_BOUND_AT';

/**
 * 处理达人合作码的服务端返回与 3 个月展示窗口（仅国内站）
 *
 * 入参 code 为商品列表接口回显的服务端「当前生效绑定码」。
 * 根据 localStorage 记录的绑定时间判断是否在 3 个月内：
 * - 新码 / 3 个月内：填入 bonusCode（入口展示 + 下单使用）
 * - 超过 3 个月：不填入，入口显示"立刻填写"，用户需手动重新填写
 *
 * 注意：校验合作码接口不会切换服务端绑定（真正切绑靠下单），
 * 所以用户编辑新码后、下单前，服务端仍回显旧码。此时任何触发拉商品列表的地方
 * （下单成功、兑换券、退订等）都会带着旧码走到这里。为避免旧码覆盖用户本会话
 * 手动设置的新码，当 store 现值已是与服务端不同的码时直接跳过回写。
 */
export const useBonusCode = () => {
  const updateBonusCode = useStore((state) => state.updateBonusCode);
  const storeApi = useStoreApi();

  const [bound, setBound] = useLocalStorageState<BonusCodeBound | undefined>(BONUS_CODE_BOUND_KEY);

  const handleBonusCode = useCallback(
    (code: string) => {
      // store 现值为用户手动设置的更新码（服务端下单前尚未同步）时，不用服务端旧码覆盖
      const current = storeApi.getState().bonusCode;
      if (current && current !== code) {
        return;
      }

      const now = Date.now();
      if (bound?.code !== code) {
        setBound({ code, boundAt: now });
        updateBonusCode(code);
      } else if (now - bound.boundAt < 3 * ONE_MONTH) {
        updateBonusCode(code);
      }
    },
    [bound],
  );

  return { handleBonusCode };
};
